/**
 * Calendar, age, and rotation math for the planner.
 *
 * Every function here is pure. Nothing reads the clock, does I/O, or draws a
 * random number — the current instant always arrives as a `now: Timestamp`
 * parameter and a caller-supplied IANA time zone, and anything that would want
 * randomness takes a seed instead. That is what makes a plan reproducible: the
 * same inputs always rebuild the same six weeks.
 *
 * Two shapes of "day" live here and they are deliberately different:
 *  - `Timestamp` is an instant (epoch ms). Turning it into a day needs a zone.
 *  - `CalendarDate` is a wall-clock day ("2026-06-01"). Arithmetic on it is
 *    immune to daylight saving, so `addDays` and `diffDays` take no zone at all.
 */

import type {
  AgeInMonths,
  Block,
  CalendarDate,
  DomainKey,
  Ritual,
  Timestamp,
} from '../types'

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

/** A cycle is six weeks. Long enough to move a skill, short enough to re-aim. */
export const CYCLE_WEEKS = 6

/** Three blocks a day is what a real day holds. */
export const DEFAULT_BLOCKS_PER_DAY = 3

/** Cycle 1 proved ten minutes is the honest number, not twenty. */
export const DEFAULT_BLOCK_MINUTES = 10

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 3_600_000
const MS_PER_DAY = 86_400_000
const DAYS_PER_WEEK = 7

const WEEKDAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

// ─────────────────────────────────────────────────────────────
// Calendar date parsing
// ─────────────────────────────────────────────────────────────

export interface DateParts {
  year: number
  /** 1..12 */
  month: number
  /** 1..31 */
  day: number
}

const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/** Days in a month. `month` is 1..12. */
export function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/** Parse "2026-06-01". Throws on anything that is not a real calendar day. */
export function parseCalendarDate(date: CalendarDate): DateParts {
  const match = CALENDAR_DATE_RE.exec(date)
  if (!match) {
    throw new RangeError(`Not a calendar date (expected YYYY-MM-DD): ${String(date)}`)
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12) {
    throw new RangeError(`Month out of range in ${date}`)
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    throw new RangeError(`Day out of range in ${date}`)
  }
  return { year, month, day }
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n))

/** Build "2026-06-01" from parts. `month` is 1..12. */
export function formatCalendarDate(year: number, month: number, day: number): CalendarDate {
  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}` as CalendarDate
}

// ─────────────────────────────────────────────────────────────
// Time zone plumbing
// ─────────────────────────────────────────────────────────────

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function zoneFormatter(tz: string): Intl.DateTimeFormat {
  const cached = formatterCache.get(tz)
  if (cached) return cached
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    throw new RangeError(`Unknown time zone: ${tz}`)
  }
  formatterCache.set(tz, formatter)
  return formatter
}

interface WallClock extends DateParts {
  hour: number
  minute: number
  second: number
}

/** The wall clock a person in `tz` reads at instant `ts`. */
function wallClock(ts: Timestamp, tz: string): WallClock {
  const parts = zoneFormatter(tz).formatToParts(new Date(ts))
  const read: Record<string, number> = {}
  for (const part of parts) {
    if (part.type !== 'literal') read[part.type] = Number(part.value)
  }
  // Some engines render midnight as hour 24 rather than 0.
  const hour = read.hour === 24 ? 0 : read.hour
  return {
    year: read.year,
    month: read.month,
    day: read.day,
    hour,
    minute: read.minute,
    second: read.second,
  }
}

/**
 * How far `tz` runs ahead of UTC at instant `ts`, in milliseconds.
 * Positive east of Greenwich. Changes across a daylight saving boundary, which
 * is exactly why every conversion below asks for it at a specific instant.
 */
export function timeZoneOffsetMs(ts: Timestamp, tz: string): number {
  const whole = Math.floor(ts / 1000) * 1000
  const local = wallClock(whole, tz)
  const asUtc = Date.UTC(
    local.year, local.month - 1, local.day, local.hour, local.minute, local.second,
  )
  return asUtc - whole
}

// ─────────────────────────────────────────────────────────────
// Instant ↔ calendar date
// ─────────────────────────────────────────────────────────────

/** Which day it is in `tz` at instant `ts`. */
export function toCalendarDate(ts: Timestamp, tz: string): CalendarDate {
  const local = wallClock(ts, tz)
  return formatCalendarDate(local.year, local.month, local.day)
}

/**
 * The instant a day begins in `tz` — normally local midnight.
 *
 * On a spring-forward day where midnight itself is skipped, this returns the
 * first instant that actually falls on the requested day.
 */
export function fromCalendarDate(date: CalendarDate, tz: string): Timestamp {
  const { year, month, day } = parseCalendarDate(date)
  const wallAsUtc = Date.UTC(year, month - 1, day)
  // Two passes: guess with the offset at the naive instant, then re-read the
  // offset at the corrected one. The second pass fixes days that straddle a
  // daylight saving change.
  let ts = wallAsUtc - timeZoneOffsetMs(wallAsUtc, tz)
  ts = wallAsUtc - timeZoneOffsetMs(ts, tz)
  if (toCalendarDate(ts, tz) === date) return ts
  for (let hours = 1; hours <= 6; hours++) {
    const forward = ts + hours * MS_PER_HOUR
    if (toCalendarDate(forward, tz) === date) return forward
    const back = ts - hours * MS_PER_HOUR
    if (toCalendarDate(back, tz) === date) return back
  }
  return ts
}

/** The instant the day containing `ts` began in `tz`. */
export function startOfDay(ts: Timestamp, tz: string): Timestamp {
  return fromCalendarDate(toCalendarDate(ts, tz), tz)
}

/** Do two instants land on the same day for someone living in `tz`? */
export function isSameDay(a: Timestamp, b: Timestamp, tz: string): boolean {
  return toCalendarDate(a, tz) === toCalendarDate(b, tz)
}

// ─────────────────────────────────────────────────────────────
// Calendar arithmetic (zone-free by design)
// ─────────────────────────────────────────────────────────────

/** Move a calendar day forward (or back, with a negative count). */
export function addDays(date: CalendarDate, days: number): CalendarDate {
  if (!Number.isInteger(days)) {
    throw new RangeError(`addDays needs a whole number of days, got ${days}`)
  }
  const { year, month, day } = parseCalendarDate(date)
  const moved = new Date(Date.UTC(year, month - 1, day) + days * MS_PER_DAY)
  return formatCalendarDate(moved.getUTCFullYear(), moved.getUTCMonth() + 1, moved.getUTCDate())
}

/** Whole days from `from` to `to`. Negative when `to` is earlier. */
export function diffDays(from: CalendarDate, to: CalendarDate): number {
  const a = parseCalendarDate(from)
  const b = parseCalendarDate(to)
  const aMs = Date.UTC(a.year, a.month - 1, a.day)
  const bMs = Date.UTC(b.year, b.month - 1, b.day)
  return Math.round((bMs - aMs) / MS_PER_DAY)
}

/** 0 = Sunday … 6 = Saturday. */
export function weekdayIndex(date: CalendarDate): number {
  const { year, month, day } = parseCalendarDate(date)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

/** "Monday". Always English — these strings are keys, not display copy. */
export function weekdayName(date: CalendarDate): string {
  return WEEKDAY_NAMES[weekdayIndex(date)]
}

/**
 * The first day of the week containing `date`.
 * Weeks start on Monday unless the caller says otherwise (0 = Sunday).
 */
export function startOfWeek(date: CalendarDate, weekStartsOn: number = 1): CalendarDate {
  if (!Number.isInteger(weekStartsOn) || weekStartsOn < 0 || weekStartsOn > 6) {
    throw new RangeError(`weekStartsOn must be 0..6, got ${weekStartsOn}`)
  }
  const back = (weekdayIndex(date) - weekStartsOn + DAYS_PER_WEEK) % DAYS_PER_WEEK
  return addDays(date, -back)
}

/** "Tuesday, June 2" — the way a parent would say it out loud. */
export function formatFriendly(
  date: CalendarDate,
  options: { includeYear?: boolean } = {},
): string {
  const { year, month, day } = parseCalendarDate(date)
  const head = `${weekdayName(date)}, ${MONTH_NAMES[month - 1]} ${day}`
  return options.includeYear ? `${head}, ${year}` : head
}

// ─────────────────────────────────────────────────────────────
// Age
// ─────────────────────────────────────────────────────────────

/**
 * Age in whole months, derived — never stored.
 *
 * A month turns over on the matching day of the month. When the target month is
 * too short for that day, the last day of the month counts: a child born on
 * January 31 is one month old on February 28 of a common year, because February
 * has no 31st and the end of the month is the anniversary.
 *
 * The label reads "2y 9m", or "9m" under a year, or "3y" on a whole birthday.
 * A birth date in the future clamps to zero rather than reporting a negative age.
 */
export function ageInMonths(
  birthDate: CalendarDate,
  now: Timestamp,
  tz: string,
): AgeInMonths {
  const birth = parseCalendarDate(birthDate)
  const today = parseCalendarDate(toCalendarDate(now, tz))

  let months = (today.year - birth.year) * 12 + (today.month - birth.month)
  const anniversaryDay = Math.min(birth.day, daysInMonth(today.year, today.month))
  if (today.day < anniversaryDay) months -= 1
  if (months < 0) months = 0

  const years = Math.floor(months / 12)
  const remainderMonths = months % 12
  let label: string
  if (years === 0) label = `${remainderMonths}m`
  else if (remainderMonths === 0) label = `${years}y`
  else label = `${years}y ${remainderMonths}m`

  return { months, years, remainderMonths, label }
}

// ─────────────────────────────────────────────────────────────
// Sleeps — the time ritual's engine
// ─────────────────────────────────────────────────────────────

/**
 * Nights between now and `target`. Zero means today, one means tomorrow, and a
 * negative number means the day has passed. Counting sleeps is how a child this
 * age can actually hold onto time.
 */
export function sleepsUntil(target: CalendarDate, now: Timestamp, tz: string): number {
  return diffDays(toCalendarDate(now, tz), target)
}

/** The sentence a parent says at breakfast. `event` is a plain noun phrase. */
export function sleepsLabel(sleeps: number, event: string): string {
  if (sleeps === 0) return `${event} is today!`
  if (sleeps === 1) return `One more sleep until ${event}.`
  if (sleeps > 1) return `${sleeps} sleeps until ${event}.`
  if (sleeps === -1) return `${event} was yesterday.`
  return `${event} was ${Math.abs(sleeps)} sleeps ago.`
}

// ─────────────────────────────────────────────────────────────
// Cycle skeleton
// ─────────────────────────────────────────────────────────────

export interface DaySkeleton {
  date: CalendarDate
  /** The instant this day starts in the given zone. */
  startsAt: Timestamp
  /** 1-based, matches `Week.number`. */
  weekNumber: number
  /** 0-based position inside the whole cycle. */
  dayIndex: number
  /** 0-based position inside its week. */
  dayOfWeek: number
  weekday: string
}

export interface WeekSkeleton {
  /** 1-based, matches `Week.number`. */
  number: number
  startDate: CalendarDate
  endDate: CalendarDate
  days: DaySkeleton[]
}

export interface CycleSkeleton {
  startDate: CalendarDate
  endDate: CalendarDate
  weeks: WeekSkeleton[]
}

/**
 * The empty calendar a cycle is planned into: `weeks` seven-day weeks running
 * from `startDate`. Week 1 begins on `startDate` whatever weekday that is — the
 * cycle owns its own rhythm rather than inheriting the calendar's.
 */
export function generateCycleSkeleton(
  startDate: CalendarDate,
  weeks: number,
  tz: string,
): CycleSkeleton {
  if (!Number.isInteger(weeks) || weeks < 1) {
    throw new RangeError(`A cycle needs at least one week, got ${weeks}`)
  }
  parseCalendarDate(startDate)

  const weekList: WeekSkeleton[] = []
  for (let w = 0; w < weeks; w++) {
    const days: DaySkeleton[] = []
    for (let d = 0; d < DAYS_PER_WEEK; d++) {
      const dayIndex = w * DAYS_PER_WEEK + d
      const date = addDays(startDate, dayIndex)
      days.push({
        date,
        startsAt: fromCalendarDate(date, tz),
        weekNumber: w + 1,
        dayIndex,
        dayOfWeek: d,
        weekday: weekdayName(date),
      })
    }
    weekList.push({
      number: w + 1,
      startDate: days[0].date,
      endDate: days[DAYS_PER_WEEK - 1].date,
      days,
    })
  }

  return {
    startDate,
    endDate: weekList[weeks - 1].endDate,
    weeks: weekList,
  }
}

// ─────────────────────────────────────────────────────────────
// Block rotation
// ─────────────────────────────────────────────────────────────

function hashSeed(seed: string | number): number {
  const text = String(seed)
  let hash = 2166136261
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/**
 * Hand out `slots` places among `order`, giving everyone one place first and
 * then favouring the domains at the front of the list. No domain may hold more
 * than `cap` places, because a domain can appear at most once a day.
 */
function shareOut(
  size: number,
  priorityCount: number,
  slots: number,
  cap: number,
): number[] {
  const counts = new Array<number>(size).fill(0)

  // Coverage first: everyone gets one, in order, until the slots run out.
  let remaining = slots
  for (let i = 0; i < size && remaining > 0; i++) {
    counts[i] = 1
    remaining -= 1
  }
  if (remaining === 0) return counts

  // Then weight the rest towards the priority domains, most for the first one.
  const weights = new Array<number>(size)
  let totalWeight = 0
  for (let i = 0; i < size; i++) {
    weights[i] = i < priorityCount ? priorityCount - i : 0
    totalWeight += weights[i]
  }
  if (totalWeight === 0) {
    weights.fill(1)
    totalWeight = size
  }

  const exact = weights.map((w) => (remaining * w) / totalWeight)
  const extra = exact.map((value) => Math.floor(value))
  let handedOut = extra.reduce((sum, value) => sum + value, 0)

  // Largest remainder, with the list order breaking ties so it stays deterministic.
  const byRemainder = extra
    .map((value, i) => ({ i, fraction: exact[i] - value }))
    .sort((a, b) => (b.fraction - a.fraction) || (a.i - b.i))
  for (let k = 0; handedOut < remaining; k++) {
    extra[byRemainder[k].i] += 1
    handedOut += 1
  }
  for (let i = 0; i < size; i++) counts[i] += extra[i]

  // Nobody may exceed one appearance per day; the surplus goes back out in order.
  let surplus = 0
  for (let i = 0; i < size; i++) {
    if (counts[i] > cap) {
      surplus += counts[i] - cap
      counts[i] = cap
    }
  }
  while (surplus > 0) {
    let moved = false
    for (let i = 0; i < size && surplus > 0; i++) {
      if (counts[i] < cap) {
        counts[i] += 1
        surplus -= 1
        moved = true
      }
    }
    if (!moved) {
      throw new RangeError('Not enough distinct domains to fill the week without repeats')
    }
  }
  return counts
}

/**
 * Lay out which domain each block of each day belongs to.
 *
 * The result is indexed `[dayIndex][slotIndex]`; slot index 0 is the day's first
 * block, so `Block.slot` is the index plus one. Three promises hold:
 *
 *  1. Priority domains come up most often, the first priority most of all.
 *  2. A domain never appears twice in the same day.
 *  3. Every domain appears at least once in each seven-day week, whenever the
 *     week has room for it (`daysCount * blocksPerDay >= domains.length`). When
 *     it does not, coverage rotates across weeks so no domain is left out twice
 *     in a row.
 *
 * Deterministic: the same arguments and seed always produce the same layout.
 */
export function balanceBlockRotation(
  domains: readonly DomainKey[],
  priorities: readonly DomainKey[],
  daysCount: number,
  blocksPerDay: number,
  seed: string | number = 0,
): DomainKey[][] {
  if (domains.length === 0) {
    throw new RangeError('balanceBlockRotation needs at least one domain')
  }
  if (new Set(domains).size !== domains.length) {
    throw new RangeError('balanceBlockRotation needs distinct domains')
  }
  if (!Number.isInteger(daysCount) || daysCount < 1) {
    throw new RangeError(`daysCount must be a positive whole number, got ${daysCount}`)
  }
  if (!Number.isInteger(blocksPerDay) || blocksPerDay < 1) {
    throw new RangeError(`blocksPerDay must be a positive whole number, got ${blocksPerDay}`)
  }
  if (blocksPerDay > domains.length) {
    throw new RangeError(
      `${blocksPerDay} blocks a day needs at least ${blocksPerDay} domains to avoid repeating one`,
    )
  }

  const inPlan = new Set(domains)
  const ranked: DomainKey[] = []
  for (const domain of priorities) {
    if (inPlan.has(domain) && !ranked.includes(domain)) ranked.push(domain)
  }
  const rest = domains.filter((domain) => !ranked.includes(domain))

  const rotation: DomainKey[][] = []
  let cursor = rest.length > 0 ? hashSeed(seed) % rest.length : 0

  for (let dayStart = 0; dayStart < daysCount; dayStart += DAYS_PER_WEEK) {
    const days = Math.min(DAYS_PER_WEEK, daysCount - dayStart)
    const slots = days * blocksPerDay

    const rotated = rest.map((_, i) => rest[(cursor + i) % rest.length])
    const order = [...ranked, ...rotated]
    const counts = shareOut(order.length, ranked.length, slots, days)

    // Busiest domain first, so the top priority owns the first block of the day.
    const placement = counts
      .map((count, i) => ({ i, count }))
      .filter((entry) => entry.count > 0)
      .sort((a, b) => (b.count - a.count) || (a.i - b.i))

    const week: DomainKey[][] = []
    for (let d = 0; d < days; d++) week.push([])

    // Fill day by day across each slot before moving to the next slot. Because a
    // domain's places are consecutive and it never holds more than `days` of
    // them, its places always land on different days.
    let position = 0
    for (const entry of placement) {
      for (let n = 0; n < entry.count; n++) {
        week[position % days].push(order[entry.i])
        position += 1
      }
    }
    for (const day of week) rotation.push(day)

    let covered = 0
    for (let i = ranked.length; i < counts.length; i++) {
      if (counts[i] > 0) covered += 1
    }
    if (rest.length > 0) {
      // Advance to the first domain this week could not reach. When everything
      // was reached, step one further so weeks do not repeat verbatim.
      cursor = (cursor + (covered === rest.length ? covered + 1 : covered)) % rest.length
    }
  }

  return rotation
}

// ─────────────────────────────────────────────────────────────
// Rituals
// ─────────────────────────────────────────────────────────────

/**
 * The three things woven into an ordinary day. These are not blocks: nobody
 * sits down for them, and they cost about seven minutes between them.
 */
export const RITUALS = [
  {
    key: 'time_calendar',
    title: 'Calendar & Sleeps',
    description:
      'At breakfast, name the day together: what is today, what was yesterday, what comes tomorrow. Then the question she loves — "how many sleeps until Grandma visits?" — and count them out on fingers. Finish by pointing at the clock once and naming the hour: "It is 8 o\'clock, breakfast time." Counting sleeps is what turns time from a word into something she can hold.',
    estimatedMinutes: 2,
  },
  {
    key: 'write_and_label',
    title: 'One Real Piece of Writing',
    description:
      'Once a day, let her write something that does a job: a label on her drawing, a name on a card, a word added to the shopping list, a sign for her door. Spell it however she hears it — KAT for cat is exactly right — and say out loud which sounds she caught. Sand, chalk, and a whiteboard all count. Writing inside drawing is the version she reaches for on her own.',
    estimatedMinutes: 2,
  },
  {
    key: 'caregiving',
    title: 'Teach Someone',
    description:
      'Give her someone to look after. "Show Teddy how to count to ten." "Read this page to the baby." "Teddy seems sad — what could help?" Teaching is the deepest practice there is, and it turns repetition into something she asks to do again. Caring for someone is where she is most herself, so send the day\'s new skill through it.',
    estimatedMinutes: 3,
  },
] as const satisfies readonly Ritual[]

/** Look up a built-in ritual by key. */
export function ritualByKey(key: string): Ritual | undefined {
  return RITUALS.find((ritual) => ritual.key === key)
}

// ─────────────────────────────────────────────────────────────
// Day cost
// ─────────────────────────────────────────────────────────────

/**
 * What today actually asks of a parent, in minutes: every block plus every
 * ritual. Callers filter first if they want to leave something out.
 */
export function estimateDayMinutes(
  blocks: ReadonlyArray<Pick<Block, 'estimatedMinutes'>>,
  rituals: readonly Ritual[],
): number {
  let total = 0
  for (const block of blocks) {
    if (Number.isFinite(block.estimatedMinutes)) total += block.estimatedMinutes
  }
  for (const ritual of rituals) {
    if (Number.isFinite(ritual.estimatedMinutes)) total += ritual.estimatedMinutes
  }
  return Math.round(total)
}

/** Minutes as the app says them: "35 min", "1 h 5 min". */
export function formatMinutes(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes))
  if (whole < 60) return `${whole} min`
  const hours = Math.floor(whole / 60)
  const rest = whole % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

/** Exported for callers that want to reason in milliseconds. */
export const MS = {
  minute: MS_PER_MINUTE,
  hour: MS_PER_HOUR,
  day: MS_PER_DAY,
} as const
